/**
 * Apply the salon's webStoreTheme to document.documentElement so CSS vars
 * (--color-orange, etc.) match the shop / product surfaces.
 */
import { useEffect, useState } from 'react';
import { getSalonBySlug } from '../api/storeApi';
import { journeySetBusiness } from '../utils/shopJourney';
// The one list — utils/webStoreThemes; this file used to keep its own copy.
import { WEB_STORE_THEMES as VALID_THEMES } from '../utils/webStoreThemes';

/**
 * Applies the theme AND hands the fetched salon back to the caller.
 *
 * The return value exists so the shop's secondary pages (a collection, the
 * all-collections grid) can read the owner's page-background mode without
 * asking the server a second time for a payload this hook already has. It is
 * additive: every existing caller ignores it.
 */
export function useSalonWebTheme(salonSlug) {
  const [salonInfo, setSalonInfo] = useState(null);

  useEffect(() => {
    if (!salonSlug) {
      setSalonInfo(null);
      return undefined;
    }
    let cancelled = false;
    getSalonBySlug(salonSlug)
      ?.then((s) => {
        if (cancelled) return;
        const salon = s?.salon ?? s;
        const theme = salon?.webStoreTheme;
        if (theme && VALID_THEMES.includes(theme)) {
          document.documentElement.setAttribute('data-theme', theme);
        }
        setSalonInfo(salon ?? null);
        // Whose pages these are, so every shopper's steps are kept — not only an ad's (utils/shopJourney.js).
        try { journeySetBusiness({ salonId: salon?.id, slug: salonSlug }); } catch (_) { /* never into the page */ }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [salonSlug]);

  return salonInfo;
}

export default useSalonWebTheme;
